import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FlexFieldService } from 'src/app/shared/flex-fields/flex-field.service';
import { FlexFieldConfig, FlexFieldDataType } from 'src/app/shared/flex-fields/flex-field.model';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressBarModule } from 'primeng/progressbar';
import { AccordionModule } from 'primeng/accordion';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-flex-field-config-manager',
    standalone: true,
    templateUrl: './flex-field-config-manager.component.html',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ToastModule,
        FieldsetModule,
        InputTextModule,
        ButtonModule,
        SelectModule,
        ToggleSwitchModule,
        ProgressBarModule,
        AccordionModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule
    ],
    providers: [MessageService]
})
export class FlexFieldConfigManagerComponent implements OnInit {

    form: FormGroup;

    isLoading = false;
    isSaving = false;
    owningCompanyId?: number;
    readonly supportedBusinessObjects = [
        'SALES_ORDER',
        'SALES_ORDER_LINE',
        'PURCHASE_ORDER',
        'PURCHASE_ORDER_LINE',
        'SHIPMENT_DELIVERY'
    ];

    readonly dataTypeOptions = [
        { label: 'String', value: 'STRING' as FlexFieldDataType },
        { label: 'Integer', value: 'INTEGER' as FlexFieldDataType },
        { label: 'Decimal', value: 'DECIMAL' as FlexFieldDataType },
        { label: 'Date', value: 'DATE' as FlexFieldDataType },
        { label: 'Date & Time', value: 'DATETIME' as FlexFieldDataType },
        { label: 'Boolean', value: 'BOOLEAN' as FlexFieldDataType }
    ];

    constructor(
        private readonly fb: FormBuilder,
        private readonly flexFieldService: FlexFieldService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly messageService: MessageService
    ) {
        this.form = this.fb.group({
            businessObjects: this.fb.array<FormGroup>([])
        });
    }

    ngOnInit(): void {
        this.owningCompanyId = this.sessionStorageService.getCompany()?.id ?? undefined;
        this.loadConfigs();
    }

    get businessObjects(): FormArray<FormGroup> {
        return this.form.get('businessObjects') as FormArray<FormGroup>;
    }

    loadConfigs(): void {
        this.isLoading = true;
        this.flexFieldService.getAllConfigs(this.owningCompanyId).subscribe({
            next: (configs) => {
                this.populateForm(configs);
                this.isLoading = false;
            },
            error: (error) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error loading flex fields',
                    detail: error?.message ?? 'Unable to retrieve flex field configurations.'
                });
            }
        });
    }

    save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const payloads = this.businessObjects.controls
            .map(group => {
                const businessObject = group.get('businessObject')?.value as string;
                const configsArray = (group.get('configs') as FormArray).value as FlexFieldConfig[];
                return {
                    businessObject,
                    configs: configsArray.map(config => ({
                        ...config,
                        fieldLength: config.fieldLength === undefined || config.fieldLength === null
                            ? null
                            : Number(config.fieldLength),
                        businessObject,
                        owningCompanyId: config.owningCompanyId ?? this.owningCompanyId ?? null
                    }))
                };
            })
            .filter(item => item.businessObject && item.configs.length);

        if (!payloads.length) {
            this.messageService.add({
                severity: 'info',
                summary: 'Nothing to save',
                detail: 'No flex field changes were detected.'
            });
            return;
        }

        this.isSaving = true;
        forkJoin(
            payloads.map(payload =>
                this.flexFieldService.saveConfigs(payload.businessObject, payload.configs, this.owningCompanyId)
            )
        ).subscribe({
            next: () => {
                this.isSaving = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Flex fields updated',
                    detail: 'Flex field configuration saved successfully.'
                });
                this.loadConfigs();
            },
            error: (error) => {
                this.isSaving = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Save failed',
                    detail: error?.message ?? 'Unable to save flex field configuration.'
                });
            }
        });
    }

    reset(): void {
        this.loadConfigs();
    }

    addConfig(groupIndex: number): void {
        const group = this.businessObjects.at(groupIndex);
        const configs = group.get('configs') as FormArray;
        const businessObject = group.get('businessObject')?.value as string;
        configs.push(this.createConfigFormGroup(this.createEmptyConfig(businessObject)));
        (this.businessObjects.at(groupIndex) as FormGroup).markAsDirty();
    }

    removeConfig(groupIndex: number, configIndex: number): void {
        const configs = this.getConfigArray(groupIndex);
        configs.removeAt(configIndex);
        (this.businessObjects.at(groupIndex) as FormGroup).markAsDirty();
    }

    getConfigArray(groupIndex: number): FormArray<FormGroup> {
        return (this.businessObjects.at(groupIndex).get('configs') as FormArray<FormGroup>);
    }

    trackBusinessObject(_index: number, control: FormGroup): string {
        return (control.get('businessObject')?.value as string) ?? `group-${_index}`;
    }

    trackConfig(_index: number, control: FormGroup): string | number {
        return control.get('configId')?.value ?? `new-${_index}`;
    }

    formatBusinessObjectLabel(businessObject: string | null | undefined): string {
        if (!businessObject) {
            return 'Unassigned';
        }
        return businessObject
            .split('_')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');
    }

    private populateForm(configs: FlexFieldConfig[]): void {
        const grouped = new Map<string, FlexFieldConfig[]>();
        configs.forEach(config => {
            const key = (config.businessObject ?? 'UNASSIGNED').toUpperCase();
            if (!grouped.has(key)) {
                grouped.set(key, []);
            }
            grouped.get(key)?.push(config);
        });

        this.supportedBusinessObjects.forEach(businessObject => {
            if (!grouped.has(businessObject)) {
                grouped.set(businessObject, []);
            }
        });

        const businessObjectArray = this.fb.array<FormGroup>([]);
        const sortedEntries = Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]));
        sortedEntries.forEach(([businessObject, configList]) => {
            businessObjectArray.push(
                this.fb.group({
                    businessObject: [businessObject],
                    configs: this.fb.array<FormGroup>(
                        configList.map(config => this.createConfigFormGroup(config))
                    )
                })
            );
        });

        this.form.setControl('businessObjects', businessObjectArray);
        this.form.markAsPristine();
    }

    private createConfigFormGroup(config: FlexFieldConfig): FormGroup {
        return this.fb.group({
            configId: [config.configId ?? null],
            businessObject: [(config.businessObject ?? 'UNASSIGNED').toUpperCase(), Validators.required],
            fieldName: [config.fieldName ?? '', Validators.required],
            alternateFieldName: [config.alternateFieldName ?? ''],
            dataType: [config.dataType ?? 'STRING', Validators.required],
            fieldLength: [config.fieldLength ?? null],
            required: [config.required ?? false],
            active: [config.active ?? true],
            owningCompanyId: [config.owningCompanyId ?? this.owningCompanyId ?? null]
        });
    }

    private createEmptyConfig(businessObject: string): FlexFieldConfig {
        return {
            configId: undefined,
            businessObject: businessObject?.toUpperCase(),
            fieldName: '',
            alternateFieldName: '',
            dataType: 'STRING',
            fieldLength: null,
            required: false,
            active: true,
            owningCompanyId: this.owningCompanyId ?? null
        };
    }
}
